/**
 * No brand: a Contributor, Viewer or client whose open workspace has no brand
 * they can reach. Only an Owner can create one (an Owner with none gets
 * FirstBrandScreen), so this says who can fix it, offers a re-check, and keeps
 * the way into their other workspaces. For a client the list is the brands
 * shared with them, so "none" means none shared yet.
 */
import { Hourglass } from "lucide-react";
import { useSwitchOrganization } from "@/app/routes/dashboard/settings/api/useOrganization";
import type { OrganizationRole, UserOrganization } from "@/types/organization.type";
import { KitButton } from "../kit/Button";
import { DetailCard, DetailHead, Fact, Facts, GroupLabel, QuietNote, ScopeTile, StatusLabel } from "./parts/SettingsParts";
import { roleForYou, roleLabel } from "./parts/roles";
import { GateAccountNav, GateFrame, GateHeading } from "./NoWorkspaceScreen";

export default function NoBrandScreen({
  membership,
  otherWorkspaces,
  checking,
  onCheckAgain,
  onLogout,
}: {
  membership: UserOrganization & { role: Exclude<OrganizationRole, "Owner"> };
  otherWorkspaces: UserOrganization[];
  checking: boolean;
  onCheckAgain: () => void;
  onLogout: () => void;
}) {
  const switchOrganization = useSwitchOrganization();
  const org = membership.organization;
  const isClient = membership.role === "Guest";

  return (
    <GateFrame>
      <GateHeading
        heading={isClient ? "No brand shared with you yet" : "No brands yet"}
        sub={
          isClient
            ? `${org.name} has not given you a brand to review. Ask the team that invited you to share one.`
            : `${org.name} has no brands yet. Ask an Owner of this workspace to create one.`
        }
      />

      <DetailCard>
        <DetailHead
          lead={<ScopeTile name={org.name} size={56} />}
          title={org.name}
          sub={`You are ${isClient ? "a client" : `a ${membership.role}`} here`}
          aside={<StatusLabel icon={Hourglass}>Waiting for a brand</StatusLabel>}
        />
        <Facts>
          <Fact label="Your role">{roleForYou(membership.role)}</Fact>
          <Fact label="Who can fix this">{isClient ? "The team that invited you" : `An Owner of ${org.name}`}</Fact>
        </Facts>
        <div className="flex items-center gap-3">
          <KitButton onClick={onCheckAgain} loading={checking}>
            Check again
          </KitButton>
        </div>
        <QuietNote>
          {isClient
            ? "A brand shows up here once the team shares it with you."
            : "A brand is a client the team makes content for. Only an Owner can add one."}
        </QuietNote>
      </DetailCard>

      {otherWorkspaces.length ? (
        <nav className="flex flex-col gap-0.5 rounded-panel bg-co-surface p-2">
          <GroupLabel>Your other workspaces</GroupLabel>
          {otherWorkspaces.map((other) => (
            <button
              key={other.organization.id}
              type="button"
              onClick={() => switchOrganization(other.organization.id)}
              className="flex items-center gap-3 rounded-row px-3 py-2.5 text-left transition-colors hover:bg-co-hover"
            >
              <ScopeTile name={other.organization.name} />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-[13.5px] font-semibold text-co-text">{other.organization.name}</span>
                <span className="text-[12px] text-co-muted">{roleLabel(other.role)}</span>
              </span>
            </button>
          ))}
        </nav>
      ) : null}

      <GateAccountNav onLogout={onLogout} />
    </GateFrame>
  );
}
